import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { message, Select } from "antd";
import { CiEdit } from "react-icons/ci";
import { FaSearch } from "react-icons/fa";
import { BiSolidTrash } from "react-icons/bi";
import TextArea from "antd/es/input/TextArea";
import { TbMessageQuestion } from "react-icons/tb";

import "./styles.scss";
import uris from "../../../../config/uris";
import { CreatePlaybookForm } from "../index";
import CustomTable from "../../../../assets/component/table";
import CustomModal from "../../../../assets/component/modal";
import { TextConcat } from "../../../../helpers/text-concat";
import { ConfirmationModal } from "../../../custom-playbook/manage-playbook";
import { customPlaybookExportReset } from "../../../../store/slices/custom-playbook/export-playbook";
import { ImportCustomPlaybookQuestionsAction, importCustomPlaybookQuestionsReset } from "../../../../store/slices/custom-playbook/importPlaybookQuestions";
import { UpdateCustomPlaybookQuestionsAction, updateCustomPlaybookQuestionsReset } from "../../../../store/slices/custom-playbook/updatePlaybookQuestions";
import { RetrieveCustomPlaybookQuestionsAction, retrieveCustomPlaybookQuestionsReset } from "../../../../store/slices/custom-playbook/retrievePlaybookQuestions";

const tableHeaders = [
    {
        title: "Theme",
        dataIndex: "theme",
        key: "theme",
        width: "15%",
        sorter: (a, b) => a.theme?.localeCompare(b.theme),
    },
    {
        title: "Question",
        dataIndex: "question",
        key: "question",
        width: "60%",
        sorter: (a, b) => a.question?.localeCompare(b.question),
        render: (text) => <TextConcat text={text} maxLength={400} />,
    },
    {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        width: "15%",
    }
]

const CustomPlaybookSection = ({ playbookId, editFormValues, showPlaybookEditModal, setShowPlaybookEditModal, setSelectedQuestion, setFormErrors, setIsSubmitted, selectedQuestion, isSubmitted, formErrors, setEditFormValues, onPlaybookRenamed, onPlaybookDeleted, showRowEditModal, setShowRowEditModal, onOwnershipUpdate, onVisibilityUpdate }) => {
    const activePlaybookId = playbookId?.value || playbookId;
    const dispatch = useDispatch();

    const [search, setSearch] = useState("");
    const [selectedTheme, setSelectedTheme] = useState(undefined);
    const [tableData, setTableData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [pendingQuestionAction, setPendingQuestionAction] = useState(null);
    const [showPlaybookDeleteModal, setShowPlaybookDeleteModal] = useState(false);

    const { data: retrievedQuestionsData, isLoading: questionsLoading } = useSelector(state => state?.retrieveCustomPlaybookQuestions) || {};
    const { success: importSuccess, error: importError, isLoading: importLoading } = useSelector(state => state?.importCustomPlaybookQuestions) || {};
    const { data: updateQuestionData, success: updateQuestionSuccess, error: updateQuestionError, isLoading: updateQuestionLoading } = useSelector(state => state?.updateCustomPlaybookQuestions) || {};
    const { isLoading: exportLoading, success: exportSuccess, error: exportError } = useSelector(state => state?.customPlaybookExport) || {};

    const navigate = useNavigate();

    const questionsData = retrievedQuestionsData?.data?.playbookQuestions?.map(item => ({
        ...item,
        questionId: item?._id,
    })) || [];

    const themeOptions = [...new Set(questionsData?.map(item => item?.theme).filter(Boolean))]
        ?.map(theme => ({ label: theme, value: theme }));

    const isOwner = retrievedQuestionsData?.data?.isOwner || false;

    const owner = retrievedQuestionsData?.data?.owner || null;
    const ownerName = (typeof owner === "string" ? owner : owner?.name) || null;

    const visibilityLabel = retrievedQuestionsData?.data?.visibility || null;

    const fetchQuestions = () => {
        if (!activePlaybookId) return;
        dispatch(RetrieveCustomPlaybookQuestionsAction({ playbookName: activePlaybookId }));
    }

    useEffect(() => {
        fetchQuestions();
        return () => dispatch(retrieveCustomPlaybookQuestionsReset());
    }, [activePlaybookId])

    const handleKeyDown = (e) => {
        if (e?.key === "Enter" && !e?.shiftKey) {
            e.preventDefault();
            searchBasedFilter();
        }
    }

    const applyFilters = (searchTerm, theme) => {
        const term = searchTerm?.trim()?.toLowerCase();
        const filteredData = questionsData?.filter(({ question, theme: rowTheme }) => {
            const questionText = question?.replace(/[\r\n]+/g, '')?.toLowerCase() || ''
            const matchesSearch = questionText?.includes(term);
            const matchesTheme = !theme || rowTheme === theme;
            return matchesSearch && matchesTheme;
        })
        setTableData({ dataSource: filteredData?.map(item => formatTableRow(item)), columns: tableHeaders })
    }

    const searchBasedFilter = () => {
        applyFilters(search, selectedTheme);
    }

    const handlethemeChange = (theme) => {
        setSelectedTheme(theme);
        applyFilters(search, theme);
    }

    const resetFilters = () => {
        setSearch("");
        setSelectedTheme(undefined);
        applyFilters("", undefined);
    }

    const handleRowSelection = ({ selectedRow }) => {
        navigate(uris.companyResearch, { state: { question: selectedRow?.question } });
    }

    const handleEditPlaybook = () => {
        setShowPlaybookEditModal(true);
    }

    const handleDeletePlaybook = () => {
        setShowPlaybookDeleteModal(true);
    }

    const handleEditClick = (row) => {
        setSelectedQuestion(row);
        setEditFormValues({
            theme: row?.theme || "",
            question: row?.question || "",
        });
        setFormErrors({});
        setIsSubmitted(false);
        setShowRowEditModal(true);
    }

    const handleEditFieldChange = (field, value) => {
        setEditFormValues(prev => ({ ...prev, [field]: value }));
        setFormErrors(prev => ({ ...prev, [field]: "" }));
    }

    const handleCloseEditModal = () => {
        setShowRowEditModal(false);
        setSelectedQuestion(null);
        setIsSubmitted(false);
    }

    const handleSaveQuestion = () => {
        setIsSubmitted(true);

        const errors = {};
        if (!editFormValues.question?.trim()) {
            errors.question = "Please enter the question";
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        if (selectedQuestion) {
            setPendingQuestionAction("update");
            dispatch(UpdateCustomPlaybookQuestionsAction({
                isPlaybookQuestion: true,
                questionId: selectedQuestion?.questionId,
                theme: editFormValues.theme?.trim(),
                question: editFormValues.question?.trim(),
            }));
            return;
        }

        const formData = new FormData();
        formData.append("playbookName", activePlaybookId);
        formData.append("theme", editFormValues.theme?.trim());
        formData.append("question", editFormValues.question?.trim());
        setPendingQuestionAction("add");
        dispatch(ImportCustomPlaybookQuestionsAction(formData));
    }

    const handleDeleteQuestion = (row) => {
        setPendingQuestionAction("delete");
        dispatch(UpdateCustomPlaybookQuestionsAction({ isPlaybookQuestion: true, questionId: row?.questionId, isDelete: true }));
    }

    const handleConfirmDelete = () => {
        const payload = {
            isPlaybook: true,
            isDelete: true,
            visibility: retrievedQuestionsData?.data?.visibility,
            currentPlaybookName: retrievedQuestionsData?.data?.playbookName
        }
        setPendingQuestionAction("deletePlaybook");
        dispatch(UpdateCustomPlaybookQuestionsAction(payload));
    }

    useEffect(() => {
        if (exportSuccess) {
            message.success("Playbook questions exported successfully");
            dispatch(customPlaybookExportReset());
        }
        if (exportError) {
            message.error(exportError?.message || "Failed to export playbook questions");
            dispatch(customPlaybookExportReset());
        }
    }, [exportSuccess, exportError])

    useEffect(() => {
        if (importSuccess) {
            message.success("Saved successfully");
            dispatch(importCustomPlaybookQuestionsReset());
            setPendingQuestionAction(null);
            handleCloseEditModal();
            fetchQuestions();
        }
        if (importError) {
            message.error(importError?.message || "Failed to save");
            dispatch(importCustomPlaybookQuestionsReset());
        }
    }, [importSuccess, importError])

    useEffect(() => {
        if (updateQuestionSuccess) {
            message.success(updateQuestionData?.message);
            dispatch(updateCustomPlaybookQuestionsReset());
            setShowPlaybookDeleteModal(false);
            if (pendingQuestionAction === "deletePlaybook") {
                setPendingQuestionAction(null);
                onPlaybookDeleted?.();
            } else {
                setPendingQuestionAction(null);
                handleCloseEditModal();
                fetchQuestions();
            }
        }
        if (updateQuestionError) {
            message.error(updateQuestionError?.message || "Failed to update question");
            dispatch(updateCustomPlaybookQuestionsReset());
        }
    }, [updateQuestionSuccess, updateQuestionError])

    const formatTableRow = (row) => {
        const ActionItems = () => {
            return (
                <div className="action-buttons-container">
                    <div className="action-button regenerate-question" onClick={() => handleRowSelection({ selectedRow: row })}>
                        Ask <TbMessageQuestion className="icon" />
                    </div>
                    {isOwner && (<div className="button-wrapper edit-button" onClick={() => handleEditClick(row)}>
                        <CiEdit />
                    </div>)}
                    {isOwner && (<div className="button-wrapper delete-button" onClick={() => handleDeleteQuestion(row)}>
                        <BiSolidTrash />
                    </div>)}
                </div>
            )
        }

        return {
            ...row,
            key: row?.questionId,
            actions: <ActionItems />,
        }
    }

    useEffect(() => {
        applyFilters(search, selectedTheme);
    }, [retrievedQuestionsData, activePlaybookId])

    useEffect(() => {
        onOwnershipUpdate?.(isOwner);
    }, [isOwner])

    useEffect(() => {
        onVisibilityUpdate?.(visibilityLabel);
    }, [visibilityLabel])

    return (
        <div className="custom-playbook-main">
            <div className="custom-playbook-filters">
                {visibilityLabel && (
                    <div className={`visibility-badge ${visibilityLabel?.toLowerCase()}`}>
                        Visibility: {visibilityLabel}
                    </div>
                )}
                {owner && (
                    <div className={`visibility-badge org`}>
                        Owner: {isOwner ? "Self" : ownerName}
                    </div>
                )}
                <div className="input-container">
                    <Select
                        showSearch
                        allowClear
                        className="filter-select"
                        options={themeOptions}
                        onChange={(theme) => handlethemeChange(theme)}
                        value={selectedTheme}
                        placeholder="Select Theme"
                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                    />
                </div>
                <div className="search-wrapper">
                    <input placeholder="Search Question" className="search-input" onChange={(e) => setSearch(e?.target?.value)} onKeyDown={(e) => handleKeyDown(e)} value={search} />
                    <FaSearch className={`search-icon ${search ? 'active-icon' : ''}`} onClick={searchBasedFilter} />
                </div>
                <div className="filter-buttons">
                    <div className="filter-button apply" onClick={searchBasedFilter}>
                        Apply
                    </div>
                    <div className="filter-button reset" onClick={() => resetFilters()}>
                        Reset
                    </div>
                </div>
            </div>
            <div className="custom-playbook-table">
                <CustomTable isLoading={questionsLoading} limit={limit} setLimit={setLimit} page={page} setPage={setPage} tableData={tableData} />
            </div>
            {showRowEditModal && (
                <CustomModal
                    handleClose={handleCloseEditModal}
                    modalTitle={selectedQuestion ? "Edit Question" : "Add Question"}
                    showCloseIcon
                >
                    <div className="edit-question-form">
                        <div className="input-wrapper">
                            <div className="input-label">Theme</div>
                            <div className="text-wrapper">
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="Enter Theme (Eg. Water Management)"
                                    value={editFormValues.theme}
                                    onChange={(e) => handleEditFieldChange("theme", e.target.value)}
                                />
                            </div>
                            {isSubmitted && formErrors.theme && <div className="error-message">{formErrors.theme}</div>}
                        </div>
                        <div className="input-wrapper">
                            <div className="input-label">Question<span>*</span></div>
                            <TextArea
                                rows={3}
                                placeholder="Enter the question"
                                value={editFormValues.question}
                                onChange={(e) => handleEditFieldChange("question", e.target.value)}
                            />
                            {isSubmitted && formErrors.question && <div className="error-message">{formErrors.question}</div>}
                        </div>
                        <div className="action-buttons">
                            <div className={`button-wrapper submit ${(importLoading || updateQuestionLoading) ? 'disabled' : ''}`} onClick={() => !(importLoading || updateQuestionLoading) && handleSaveQuestion()}>
                                {(importLoading || updateQuestionLoading) ? "Saving..." : "Save"}
                            </div>
                            <div className="button-wrapper cancel" onClick={handleCloseEditModal}>
                                Cancel
                            </div>
                        </div>
                    </div>
                </CustomModal>
            )}
            {showPlaybookEditModal && (
                <CustomModal handleClose={() => setShowPlaybookEditModal(false)} modalTitle="Edit Playbook" showCloseIcon>
                    <CreatePlaybookForm
                        handleClose={() => setShowPlaybookEditModal(false)}
                        isEditModal={true}
                        currentPlaybookName={activePlaybookId}
                        currentVisibility={visibilityLabel}
                        onUpdated={(newPlaybookName) => {
                            setShowPlaybookEditModal(false);
                            onPlaybookRenamed?.(newPlaybookName);
                        }}
                        onDeleted={() => {
                            setShowPlaybookEditModal(false);
                            onPlaybookDeleted?.();
                        }}
                    />
                </CustomModal>
            )}
            <ConfirmationModal
                showModal={showPlaybookDeleteModal}
                handleClose={() => setShowPlaybookDeleteModal(false)}
                message={
                    <>
                        Are you sure you want to delete {" "}
                        <strong>{retrievedQuestionsData?.data?.playbookName}</strong> ?
                    </>
                }
                handleConfirm={handleConfirmDelete}
            />
        </div>
    )
}

export default CustomPlaybookSection;
